import React, { useRef, useCallback, useState, useEffect } from "react";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import MapMarker from "./MapMarker";
import MapInfoWindow from "./MapInfoWindow";
import MapSearch from "./MapSearch";
import LoadingIcon from "../icons/loadingIcon";
import ConfirmationInfoWindow from "./ConfirmationInfoWindow";
import { store } from "react-notifications-component";
import notification from "../utilities/notification";
import { libraries, options, smallScreenSize } from "./mapProperties";

const Map = ({
  center,
  currentUser,
  loaded,
  locationMode,
  modalIsOpen,
  notificationId,
  postId,
  posts,
  selected,
  setLocationMode,
  setMapLoaded,
  setNotificationId,
  setPosts,
  setSelected,
  setShowMenu,
  setShowSearch,
  showSearch,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  const mapRef = useRef();
  const [inBounds, setInBounds] = useState([]);
  const [mapIdle, setMapIdle] = useState(false);
  const [newPost, setNewPost] = useState({});
  const [newPostMode, setNewPostMode] = useState(false);
  const [screen, setScreen] = useState(false);
  const [screenDimensions, setScreenDimensions] = useState({});

  useEffect(() => {
    if (!screen) {
      resizeMap();
      setScreen(true);
    }
  }, [screen]);

  useEffect(() => {
    window.onresize = () => {
      resizeMap();
    };
  }, []);

  const resizeMap = () => {
    if (smallScreenSize) {
      setScreenDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    } else {
      setScreenDimensions({
        height: "100vh",
        width: window.innerWidth - 450,
      });
    }
  };

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      setMapLoaded(true);
    },
    [setMapLoaded]
  );

  const panTo = useCallback(({ lat, lng }) => {
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(14);
  }, []);

  const onMapClick = useCallback(
    (event) => {
      if (locationMode) {
        store.removeNotification(notificationId);
        let latitude = event.latLng.lat().toString();
        let longitude = event.latLng.lng().toString();
        const post = {
          id: postId,
          type: "posts",
          attributes: {
            image: {
              latitude: latitude,
              longitude: longitude,
            },
            user: {
              name: currentUser,
            },
          },
        };
        setPosts((posts) => [...posts, post]);
        setNewPost(post);
        setNewPostMode(true);
      } else {
        setSelected(null);
        setShowMenu(false);
        setShowSearch(false);
      }
    },
    [
      currentUser,
      locationMode,
      notificationId,
      postId,
      setPosts,
      setSelected,
      setShowMenu,
      setShowSearch,
    ]
  );

  const onMapIdle = () => {
    setMapIdle(true);
  };

  const onMapBoundsChanged = () => {
    const bounds = mapRef.current.getBounds();
    const postsInBounds = posts.filter((post) => {
      let lat = parseFloat(post.attributes.image.latitude);
      let lng = parseFloat(post.attributes.image.longitude);
      let latLng = { lat: lat, lng: lng };
      return bounds.contains(latLng);
    });
    setInBounds(postsInBounds);
  };

  const boundsChangedNotification = useCallback(() => {
    const mapIsClear =
      mapIdle && !selected && !modalIsOpen && !showSearch && !newPostMode;
    if (mapIsClear) {
      const numberOfSightings = inBounds.length;
      if (numberOfSightings > 0) {
        const cats = numberOfSightings === 1 ? "cat" : "cats";
        notification(
          `${numberOfSightings} ${cats} spotted in this area`,
          "info",
          1000
        );
      } else {
        notification("No cats spotted in this area", "warning", 1000);
      }
      setMapIdle(false);
    }
  }, [
    inBounds.length,
    mapIdle,
    modalIsOpen,
    newPostMode,
    selected,
    showSearch,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      boundsChangedNotification();
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [boundsChangedNotification, inBounds]);

  if (loadError) return "Error loading map";
  if (!isLoaded) return <LoadingIcon type={"bars"} />;

  return (
    <>
      {!modalIsOpen && (
        <MapSearch
          loaded={loaded}
          panTo={panTo}
          setSelected={setSelected}
          setShowMenu={setShowMenu}
          setShowSearch={setShowSearch}
          showSearch={showSearch}
        />
      )}
      <div className="map">
        <GoogleMap
          center={center}
          mapContainerStyle={screenDimensions}
          onBoundsChanged={onMapBoundsChanged}
          onClick={onMapClick}
          onIdle={onMapIdle}
          onLoad={onMapLoad}
          options={options}
          zoom={15}
        >
          {posts.map((post) => {
            return (
              <MapMarker
                key={post.id}
                latitude={post.attributes.image.latitude}
                longitude={post.attributes.image.longitude}
                post={post}
                setSelected={setSelected}
                setShowMenu={setShowMenu}
                setShowSearch={setShowSearch}
              />
            );
          })}
          {selected ? (
            <MapInfoWindow
              latitude={selected.attributes.image.latitude}
              longitude={selected.attributes.image.longitude}
              postId={selected.id}
              setSelected={setSelected}
            />
          ) : null}
          {newPostMode ? (
            <ConfirmationInfoWindow
              latitude={newPost.attributes.image.latitude}
              longitude={newPost.attributes.image.longitude}
              newPost={newPost}
              notificationId={notificationId}
              postId={postId}
              posts={posts}
              setLocationMode={setLocationMode}
              setNewPostMode={setNewPostMode}
              setNotificationId={setNotificationId}
              setPosts={setPosts}
              setSelected={setSelected}
            />
          ) : null}
        </GoogleMap>
      </div>
    </>
  );
};

export default Map;
